export class EventManager {
  constructor(config = {}) {
    this.config = {
      debounce: 300,
      ...config
    };
    
    this.listeners = new Map();
    this.debounceTimers = new Map();
  }

  attachInputListener(input, callback) {
    const debouncedCallback = this.debounce((event) => {
      const value = event.target.value;
      // Use longer debounce for longer queries to let users finish typing
      const dynamicDebounce = value.length > 10 ? this.config.debounce * 2 : this.config.debounce;
      
      // Clear previous timer and set new one with dynamic delay
      clearTimeout(this.currentTimer);
      this.currentTimer = setTimeout(() => {
        callback(value, event);
      }, dynamicDebounce);
    }, 100); // Short delay for the debounce function itself

    const listeners = {
      input: debouncedCallback,
      focus: (event) => {
        event.target.setAttribute('data-pixl-search-active', 'true');
      },
      blur: (event) => {
        event.target.setAttribute('data-pixl-search-active', 'false');
      },
      keydown: (event) => {
        // Prevent Enter key from submitting the form
        if (event.key === 'Enter' || event.keyCode === 13) {
          event.preventDefault();
          event.stopPropagation();
          
          // Optionally trigger search immediately on Enter
          const value = event.target.value;
          if (value.trim()) {
            callback(value, event);
          }
        }
      }
    };

    input.addEventListener('input', listeners.input);
    input.addEventListener('focus', listeners.focus);
    input.addEventListener('blur', listeners.blur);
    input.addEventListener('keydown', listeners.keydown);

    this.listeners.set(input, listeners);
  }

  attachFormListener(form, callback) {
    const submitHandler = (event) => {
      // Completely prevent any form submission for search forms
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      const searchInput = form.querySelector('[pixl-search-field]');
      if (searchInput) {
        callback(searchInput.value, event);
      }
      
      // Return false for extra prevention
      return false;
    };

    // Disable form submission completely
    form.addEventListener('submit', submitHandler);
    
    // Also disable any Webflow form submission behavior
    form.setAttribute('data-wf-ignore', 'true');
    form.setAttribute('data-pixl-search-form-disabled', 'true');
    
    // Remove any existing action to prevent accidental submission
    if (form.hasAttribute('action')) {
      form.setAttribute('data-original-action', form.getAttribute('action'));
      form.removeAttribute('action');
    }
    
    this.listeners.set(form, { submit: submitHandler });
  }

  attachKeyboardShortcuts(callback) {
    const keyHandler = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        const firstInput = document.querySelector('[pixl-search-field]');
        if (firstInput) {
          firstInput.focus();
        }
      }
      
      if (event.key === 'Escape') {
        const activeInput = document.querySelector('[pixl-search-field][data-pixl-search-active="true"]');
        if (activeInput) {
          activeInput.blur();
          activeInput.value = '';
          callback('', event);
        }
      }
    };

    document.addEventListener('keydown', keyHandler);
    this.listeners.set(document, { keydown: keyHandler });
  }

  debounce(func, delay) {
    return (...args) => {
      const key = func.toString();
      
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key));
      }
      
      const timer = setTimeout(() => {
        func(...args);
        this.debounceTimers.delete(key);
      }, delay);
      
      this.debounceTimers.set(key, timer);
    };
  }

  removeListener(element) {
    const listeners = this.listeners.get(element);
    
    if (listeners) {
      Object.entries(listeners).forEach(([event, handler]) => {
        element.removeEventListener(event, handler);
      });
      
      this.listeners.delete(element);
    }
  }

  removeAllListeners() {
    this.listeners.forEach((listeners, element) => {
      this.removeListener(element);
    });
    
    this.debounceTimers.forEach(timer => clearTimeout(timer));
    this.debounceTimers.clear();
  }

  emit(eventName, data) {
    const event = new CustomEvent(`pixl-search:${eventName}`, {
      detail: data,
      bubbles: true,
      cancelable: true
    });
    
    document.dispatchEvent(event);
  }

  on(eventName, callback) {
    const handler = (event) => callback(event.detail);
    document.addEventListener(`pixl-search:${eventName}`, handler);
    
    return () => {
      document.removeEventListener(`pixl-search:${eventName}`, handler);
    };
  }
}